/* eslint-disable jsx-a11y/anchor-has-content */
import { useLocalization, Localized } from "@fluent/react";
import {
  Alert,
  Button,
  ContentCard,
  Dialog,
  Row,
  RadioButtons,
} from "@narmi/design_system";
import Account from "byzantine/src/Account";
import ApiHttp from "byzantine/src/ApiHttp";
import {
  ContextForm,
  useLoadingContext,
  useNotificationContext,
  RoutingNumberTextInput,
  ScrollingIframe,
  useFormData,
  AccountNumberTextInput,
} from "cerulean";
import PropTypes from "prop-types";
import React, {
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";
import { Route, Routes, useNavigate } from "react-router-dom";
import labeledCheck from "../../assets/images/labeled-check.png";
import InstitutionSettingsContext from "../contexts/InstitutionSettingsContext";
import {
  useAddAccountsDialogTitle,
  VERIFYROUTES,
} from "../../components/settings/linked_accounts/hooks";
import { useSudoContext } from "../../components/SudoContext";
import { useUserFeatures } from "../../components/contexts/UserFeaturesContext";

const ACCOUNT_NUMBER_MAX_LEN = 17;

const ExpectedDurationTag = ({ durationText }) => (
  <p
    style={{
      fontSize: "12px",
      color: "var(--theme-primary)",
      background: "RGBA(var(--theme-rgb-primary), var(--alpha-10))",
      padding: "2px 12px 2px 12px",
      borderRadius: "12px",
      alignSelf: "flex-start",
    }}
  >
    {durationText}
  </p>
);
ExpectedDurationTag.propTypes = {
  durationText: PropTypes.string,
};

const SelectOptions = ({
  setSrc,
  basename,
  sudoRequired,
  error,
  setError,
  instantAccVerification,
  instantVerificationOnly,
  instantAccountVerificationOnlyCancelRoute,
}) => {
  const { l10n } = useLocalization();
  const { setIsLoading } = useLoadingContext();
  const navigate = useNavigate();
  const { establishSudo, setCloseRouteOverride, setOnCancel } =
    useSudoContext();
  const isInstantOnlyMode = instantAccVerification && instantVerificationOnly;
  const hasFiredInstantOnlyRef = useRef(false);

  const onInstantSelect = useCallback(async () => {
    setIsLoading(true);
    try {
      const response = await ApiHttp.fetch(
        "signed_urls/mx/urls/connect_widget"
      );
      setSrc(response.url);
      navigate(`../${VERIFYROUTES.instantVerify}`);
    } catch (e) {
      setError(
        <Alert isActive kind="error">
          {l10n.getString("error-external-service")}
        </Alert>
      );
    }
    setIsLoading(false);
  }, [l10n, navigate, setError, setIsLoading, setSrc]);

  const onManualSelect = () => {
    setIsLoading(false);
    setError(null);
    navigate(`../${VERIFYROUTES.manualVerify}`);
  };

  // SudoWrapper in select options wrapped around both options
  // because we need to trigger sudo before the external service is called
  const onClickSudoWrapper = useCallback(
    async (destRoute, onClick) => {
      if (!sudoRequired) {
        return onClick();
      }
      setOnCancel(() => () => {
        setCloseRouteOverride(null);
      });
      setIsLoading(false);
      return establishSudo(`/${basename}/${destRoute}?`, () => {
        setIsLoading(true);
        return onClick();
      });
    },
    [
      basename,
      establishSudo,
      sudoRequired,
      setIsLoading,
      setOnCancel,
      setCloseRouteOverride,
    ]
  );

  useEffect(() => {
    // Reset the guard if a prior attempt errored so the user can retry.
    if (error) {
      hasFiredInstantOnlyRef.current = false;
    }

    if (isInstantOnlyMode && !error && !hasFiredInstantOnlyRef.current) {
      hasFiredInstantOnlyRef.current = true;
      setCloseRouteOverride(instantAccountVerificationOnlyCancelRoute);
      onClickSudoWrapper(VERIFYROUTES.instantVerify, onInstantSelect);
      return;
    }

    if (!instantAccVerification) {
      navigate(`../${VERIFYROUTES.manualVerify}`);
    }
  }, [
    instantAccVerification,
    isInstantOnlyMode,
    navigate,
    error,
    instantAccountVerificationOnlyCancelRoute,
    setCloseRouteOverride,
    onClickSudoWrapper,
    onInstantSelect,
  ]);

  return (
    <>
      {!isInstantOnlyMode && (
        <div>
          {l10n.getString(
            "add-new-bank-account-subtitle",
            null,
            "Choose how you want to add a new bank account."
          )}
        </div>
      )}
      <div className={!isInstantOnlyMode ? "margin--top--l" : ""}>
        <ContentCard
          kind="button"
          onClick={() =>
            onClickSudoWrapper(VERIFYROUTES.instantVerify, onInstantSelect)
          }
        >
          <Row>
            <Row.Item>
              <h4 className="nds-sans fontSize--l fontWeight--bold">
                {l10n.getString("title-instantly-add")}
              </h4>
            </Row.Item>
            <Row.Item shrink>
              <ExpectedDurationTag
                durationText={l10n.getString("duration-instant")}
              />
            </Row.Item>
          </Row>
          <div className="margin--top--s">
            {l10n.getString("instant-add-details")}
          </div>
        </ContentCard>
      </div>
      {!isInstantOnlyMode && (
        <div className="margin--top--l">
          <ContentCard
            kind="button"
            onClick={() =>
              onClickSudoWrapper(VERIFYROUTES.manualVerify, onManualSelect)
            }
          >
            <Row>
              <Row.Item>
                <h4 className="nds-sans fontSize--l fontWeight--bold">
                  {l10n.getString("title-manually-add")}
                </h4>
              </Row.Item>
              <Row.Item shrink>
                <ExpectedDurationTag
                  durationText={l10n.getString("duration-biz-days")}
                />
              </Row.Item>
            </Row>
            <div className="margin--top--s">
              {l10n.getString("manual-add-details")}
            </div>
          </ContentCard>
        </div>
      )}
    </>
  );
};
SelectOptions.propTypes = {
  setSrc: PropTypes.func,
  basename: PropTypes.string,
  sudoRequired: PropTypes.bool,
  error: PropTypes.element,
  setError: PropTypes.func,
  instantAccVerification: PropTypes.bool,
  instantVerificationOnly: PropTypes.bool,
  instantAccountVerificationOnlyCancelRoute: PropTypes.string,
};

const InstantForm = ({
  handleClose,
  updateAccounts,
  src,
  reloadOnVerify = true,
}) => {
  const { l10n } = useLocalization();
  const { sendNotification } = useNotificationContext();
  const { setIsLoading } = useLoadingContext();
  const navigate = useNavigate();

  const addAccounts = () =>
    ApiHttp.fetch("accounts/verify", { method: "POST" })
      .then((response) => {
        updateAccounts(response.accounts);
        if (!response.accounts.length) {
          sendNotification({
            type: "negative",
            text: l10n.getString("error-manual-add-ineligible"),
          });
          handleClose();
          return;
        }
        sendNotification({
          type: "success",
          text: l10n.getString("success-account-added"),
        });
        handleClose();

        if (reloadOnVerify) {
          // TODO (#21297): remove the 2 lines below once #21297 is fixed
          setIsLoading(true);
          document.location.reload();
        }
      })
      .catch((error) => {
        sendNotification({
          type: "negative",
          text: error || l10n.getString("error-unknown"),
        });
        handleClose();
      });

  useEffect(() => {
    const onPostMessage = (event) => {
      let postMessageData = {};
      try {
        postMessageData = JSON.parse(event.data);
      } catch (err) {
        // ignore all non-moneyDesktop events
        return;
      }
      if (!postMessageData.moneyDesktop) return;
      if (
        ["mxConnect:memberAdded", "agg_success"].indexOf(
          postMessageData.type
        ) >= 0
      ) {
        // https://www.educative.io/answers/how-to-ensure-an-event-listener-is-only-fired-once-in-javascript
        window.removeEventListener("message", onPostMessage);
        // on a message type indicating validation or a new bank membership, addAccounts
        addAccounts();
      }
    };
    window.addEventListener("message", onPostMessage);
    return () => window.removeEventListener("message", onPostMessage);
  }, []);

  useEffect(() => {
    if (!src) {
      navigate(`../${VERIFYROUTES.verifyOptions}`);
    }
  }, [src]);

  return <ScrollingIframe title="Add account form" height="550px" src={src} />;
};
InstantForm.propTypes = {
  handleClose: PropTypes.func,
  updateAccounts: PropTypes.func,
  src: PropTypes.string,
  reloadOnVerify: PropTypes.bool,
};

const ManualForm = ({
  handleClose,
  setLastFour,
  setError,
  updateAccounts,
  basename,
  sudoRequired,
}) => {
  const { l10n } = useLocalization();
  const navigate = useNavigate();
  const {
    external_accounts_allows_business_purpose: allowsBusinessPurpose,
    ach_terms_url: achTermsUrl = "",
  } = useContext(InstitutionSettingsContext);
  const { establishSudo } = useSudoContext();
  const { setIsLoading } = useLoadingContext();

  const { formData, onChange } = useFormData({
    product_type: "checking",
    account_purpose: "personal",
  });

  const handleErrors = (errors) => {
    let all;
    if (errors) {
      // errors can either be string, list of strings, or an object
      if (typeof errors === "string") {
        // the string would be the global error
        all = errors;
      } else if (Array.isArray(errors)) {
        all = errors.join(". ");
      } else {
        // treat normally
        ({ __all__: all } = errors);
      }
      if (typeof all === "string" && all.match(/[a-zA-Z0-9]$/)) {
        all += ".";
      }
    }
    all = all || l10n.getString("error-form-generic");
    return all;
  };

  const onSubmit = async (callback) => {
    if (formData.account_number.length > ACCOUNT_NUMBER_MAX_LEN) {
      callback();
      return {};
    }
    setIsLoading(false);
    return Account.addExternalAccount({ ...formData })
      .then((response) => {
        const fullString = response.accounts[0].number;
        setLastFour(fullString.substring(fullString.length - 4));
        updateAccounts(response.accounts);
        setError(null);
        navigate(`/${basename}/${VERIFYROUTES.manualComplete}`);
        callback();
      })
      .catch((error) => {
        callback(error);
        const errorMessage = handleErrors(error);
        setError(
          <Alert isActive kind="error">
            {errorMessage}
          </Alert>
        );
      });
  };

  // Still need to wrap sudo here for the case when instant verification is not enabled
  // and we navigate straight to this form
  const onSubmitSudoWrapper = async (callback) => {
    // different error handling depending on whether sudo mode is required
    if (!sudoRequired) {
      return onSubmit(callback).catch((error) => {
        callback(error);
      });
    }
    setIsLoading(false);
    return establishSudo(`/${basename}/${VERIFYROUTES.manualComplete}`, () => {
      setIsLoading(true);
      return onSubmit(callback).catch((error) => {
        callback(error);
      });
    });
  };

  return (
    <>
      <div>{l10n.getString("manual-add-form-instructions")}</div>
      <div style={{ display: "flex", justifyContent: "center" }}>
        <img
          alt={l10n.getString("alt-check-routing")}
          src={labeledCheck}
          style={{ width: "422px", marginTop: "20px", marginBottom: "20px" }}
        />
      </div>
      <ContextForm data={formData} onChange={onChange} shouldHideAllToasts>
        <ContextForm.Field required>
          <RoutingNumberTextInput
            label={l10n.getString("account-detail-routing-number")}
            field="routing_number"
            id="routing_number"
          />
        </ContextForm.Field>
        <ContextForm.Field required>
          <AccountNumberTextInput
            label={l10n.getString("account-detail-account-number")}
            field="account_number"
            id="account_number"
            maxLength={ACCOUNT_NUMBER_MAX_LEN}
          />
        </ContextForm.Field>
        <>
          <div className="radio-group-title">
            {l10n.getString("radio-title-account-type")}
          </div>
          <ContextForm.Field required>
            <RadioButtons
              field="product_type"
              name="product_type"
              options={{
                [l10n.getString("account-type-checking")]: "checking",
                [l10n.getString("account-type-savings")]: "savings",
              }}
            />
          </ContextForm.Field>
        </>
        {allowsBusinessPurpose && (
          <>
            <div className="radio-group-title">
              {l10n.getString("radio-title-account-purpose")}
            </div>
            <ContextForm.Field required>
              <RadioButtons
                field="account_purpose"
                name="account_purpose"
                options={{
                  [l10n.getString("account-purpose-personal")]: "personal",
                  [l10n.getString("account-purpose-business")]: "business",
                }}
              />
            </ContextForm.Field>
          </>
        )}
        <Localized
          id="tos-efta"
          elems={{
            termsLink: (
              <a href={achTermsUrl} target="_blank" rel="noreferrer" />
            ),
          }}
        >
          <div
            style={{ color: "var(--font-color-secondary)" }}
            className="fontSize--s"
          >
            {`
          By clicking "Link bank account", you acknowledge that you
          agree to the terms of the <termsLink>electronic funds transfer agreement</termsLink> .
          `}
          </div>
        </Localized>
        <ContextForm.ActionBar
          style={{
            marginTop: "40px",
            display: "flex",
            flexDirection: "row",
            alignItems: "center",
            justifyContent: "flex-end",
            gap: "16px",
          }}
        >
          <ContextForm.Action
            noValidation
            onSubmit={handleClose}
            dangerouslyDisableShowLoading
          >
            <Button
              kind="negative"
              label={l10n.getString("button-cancel")}
              testId="cancel-link-bank-account"
              type="button"
            />
          </ContextForm.Action>
          <ContextForm.Action onSubmit={onSubmitSudoWrapper}>
            <Button
              label={l10n.getString("button-link-bank-account")}
              testId="confirm-link-bank-account"
            />
          </ContextForm.Action>
        </ContextForm.ActionBar>
      </ContextForm>
    </>
  );
};
ManualForm.propTypes = {
  handleClose: PropTypes.func,
  setLastFour: PropTypes.func,
  setError: PropTypes.func,
  updateAccounts: PropTypes.func,
  basename: PropTypes.string,
  sudoRequired: PropTypes.bool,
};

const ManualComplete = ({ lastFour, error }) => {
  const { l10n } = useLocalization();
  const navigate = useNavigate();
  const { short_name } = useContext(InstitutionSettingsContext);

  useEffect(() => {
    if (error) {
      navigate(`../${VERIFYROUTES.manualVerify}`);
    }
  }, [error]);

  return (
    <div data-testid="external-account-micro-deposits-in-progress">
      {l10n.getString("micro-deposits-in-progress", {
        accountName: short_name,
        lastFour,
      })}
    </div>
  );
};

ManualComplete.propTypes = {
  lastFour: PropTypes.string,
  error: PropTypes.element,
};

const AddAccountModal = ({
  open,
  handleClose,
  updateAccounts,
  basename,
  addAccountMode = "default",
  instantAccountVerificationOnlyCancelRoute = "/dashboard",
}) => {
  const { l10n } = useLocalization();
  const { title, titleFluentId } = useAddAccountsDialogTitle(
    basename,
    addAccountMode
  );
  const instantVerificationOnly = addAccountMode !== "default";

  const [src, setSrc] = useState("");
  const [lastFour, setLastFour] = useState("");
  const { sudo_mode_required_for_add_external_account: sudoRequired } =
    useContext(InstitutionSettingsContext);
  const { instant_account_verification: instantAccVerification } =
    useUserFeatures();
  const [error, setError] = useState(null);

  const onUserDismiss = () => {
    setError(null);
    handleClose();
  };

  return (
    <>
      <Dialog
        isOpen={open}
        onUserDismiss={onUserDismiss}
        title={l10n.getString(titleFluentId, null, title)}
        notification={error}
        testId="add-account-modal"
      >
        <Routes>
          <Route
            path={`/${VERIFYROUTES.verifyOptions}`}
            element={
              <SelectOptions
                setSrc={setSrc}
                basename={basename}
                sudoRequired={sudoRequired}
                error={error}
                setError={setError}
                instantAccVerification={instantAccVerification}
                instantVerificationOnly={instantVerificationOnly}
                instantAccountVerificationOnlyCancelRoute={
                  instantAccountVerificationOnlyCancelRoute
                }
              />
            }
          />
          <Route
            path={`/${VERIFYROUTES.instantVerify}`}
            element={
              <InstantForm
                src={src}
                reloadOnVerify={!instantVerificationOnly}
                handleClose={onUserDismiss}
                updateAccounts={updateAccounts}
              />
            }
          />
          <Route
            path={`/${VERIFYROUTES.manualVerify}`}
            element={
              <ManualForm
                handleClose={onUserDismiss}
                setLastFour={setLastFour}
                setError={setError}
                updateAccounts={updateAccounts}
                basename={basename}
                sudoRequired={sudoRequired}
              />
            }
          />
          <Route
            path={`/${VERIFYROUTES.manualComplete}`}
            element={<ManualComplete lastFour={lastFour} error={error} />}
          />
        </Routes>
      </Dialog>
    </>
  );
};
AddAccountModal.propTypes = {
  open: PropTypes.bool,
  handleClose: PropTypes.func,
  updateAccounts: PropTypes.func,
  basename: PropTypes.string,
  addAccountMode: PropTypes.oneOf([
    "default",
    "upgradeInstantVerification",
    "revalidateInstantVerification",
  ]),
  instantAccountVerificationOnlyCancelRoute: PropTypes.string,
};

AddAccountModal.defaultProps = {
  addAccountMode: "default",
};

export default AddAccountModal;
